import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  Email as EmailIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { campaignApi, analyticsApi, templateApi, emailConfigApi, getDefaultAccountId } from '../services/api';
import { AlertInfo } from '../types';
import TiptapEditor from './TiptapEditor';

interface CampaignDetailProps {
  campaignId: string;
  onBack: () => void;
  showAlert: (message: string, severity: AlertInfo['severity']) => void;
}

interface Campaign {
  id: string;
  name: string;
  subject: string;
  content: string;
  status: string;
  createdAt: string;
  sentAt?: string;
  isFollowUp: boolean;
  parentCampaignId?: string;
  emailConfigId?: string;
}

interface FollowUp {
  id: string;
  name: string;
  subject: string;
  status: string;
  createdAt: string;
  sentAt?: string;
  analytics: {
    totalSent: number;
    totalOpened: number;
    totalClicked: number;
    openRate: number;
    clickRate: number;
  };
}

interface Recipient {
  id: string;
  contactId: string;
  contact: {
    email: string;
    firstName?: string;
    lastName?: string;
  };
  engagement: {
    opened: boolean;
    clicked: boolean;
  };
}

const CampaignDetail: React.FC<CampaignDetailProps> = ({ campaignId, onBack, showAlert }) => {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  
  // Follow-up dialog state
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [followUpName, setFollowUpName] = useState('');
  const [followUpSubject, setFollowUpSubject] = useState('');
  const [followUpContent, setFollowUpContent] = useState('');
  const [followUpTemplate, setFollowUpTemplate] = useState('');
  const [recipientFilter, setRecipientFilter] = useState('unopened');
  const [followUpStatus, setFollowUpStatus] = useState('DRAFT');
  const [filteredRecipients, setFilteredRecipients] = useState<Recipient[]>([]);

  useEffect(() => {
    loadCampaignData();
    loadTemplates();
  }, [campaignId]);

  const loadCampaignData = async () => {
    try {
      setLoading(true);
      
      // Load campaign details with analytics
      const analyticsData = await analyticsApi.getCampaignDetails(campaignId, true);
      setCampaign(analyticsData.campaign);
      setAnalytics(analyticsData.analytics);
      
      // Load follow-ups
      const followUpsData = await campaignApi.getFollowUps(campaignId);
      setFollowUps(followUpsData.followUps || []);
      
      // Load recipients
      const recipientsData = await campaignApi.getRecipients(campaignId, 'all');
      setRecipients(recipientsData.recipients || []);
      
    } catch (error) {
      console.error('Error loading campaign data:', error);
      showAlert('Failed to load campaign data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const accountId = getDefaultAccountId();
      const response = await templateApi.getByAccount(accountId);
      setTemplates(response.templates || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleCreateFollowUp = async () => {
    if (!campaign) return;

    // First, validate that the campaign's email config is still valid
    if (!campaign.emailConfigId) {
      showAlert('Cannot create follow-up: Original campaign has no email configuration.', 'error');
      return;
    }

    try {
      // Validate the email configuration
      const configValidation = await emailConfigApi.validateConfig(campaign.emailConfigId);

      if (!configValidation.success) {
        showAlert(
          `Cannot create follow-up: The email configuration used for the original campaign is no longer valid. ` +
          `Error: ${configValidation.message || 'Configuration validation failed'}. ` +
          `Please fix the email configuration before creating follow-ups.`,
          'error'
        );
        return;
      }
    } catch (error) {
      console.error('Error validating email config:', error);
      showAlert(
        'Cannot create follow-up: Unable to validate the email configuration. ' +
        'Please ensure the email configuration used for the original campaign is still active and valid.',
        'error'
      );
      return;
    }

    setFollowUpName(`${campaign.name} - Follow-up`);
    setFollowUpSubject(`Re: ${campaign.subject}`);
    setFollowUpContent('');
    setFollowUpTemplate('');
    setRecipientFilter('unopened');
    setFollowUpStatus('DRAFT');
    setFollowUpDialogOpen(true);

    // Load filtered recipients
    loadFilteredRecipients('unopened');
  };

  const loadFilteredRecipients = async (filter: string) => {
    try {
      const recipientsData = await campaignApi.getRecipients(campaignId, filter);
      setFilteredRecipients(recipientsData.recipients || []);
    } catch (error) {
      console.error('Error loading filtered recipients:', error);
    }
  };

  const handleRecipientFilterChange = (filter: string) => {
    setRecipientFilter(filter);
    loadFilteredRecipients(filter);
  };

  const handleTemplateChange = (templateId: string) => {
    setFollowUpTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFollowUpSubject(template.subject);
      setFollowUpContent(template.content);
    }
  };

  const handleCreateFollowUpSubmit = async () => {
    try {
      if (!followUpName || !followUpSubject || !followUpContent) {
        showAlert('Please fill in all required fields', 'error');
        return;
      }

      const followUpData = {
        name: followUpName,
        subject: followUpSubject,
        content: followUpContent,
        recipientFilter,
        templateId: followUpTemplate || undefined,
        status: followUpStatus
      };

      await campaignApi.createFollowUp(campaignId, followUpData);
      
      showAlert('Follow-up campaign created successfully', 'success');
      setFollowUpDialogOpen(false);
      loadCampaignData(); // Reload data
      
    } catch (error) {
      console.error('Error creating follow-up:', error);
      showAlert('Failed to create follow-up campaign', 'error');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SENT': return 'success';
      case 'DRAFT': return 'default';
      case 'SENDING': return 'warning';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Campaign not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            {campaign.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip 
              label={campaign.status} 
              color={getStatusColor(campaign.status) as any}
              size="small" 
            />
            {campaign.isFollowUp && (
              <Chip label="Follow-up" color="info" size="small" />
            )}
            <Typography variant="body2" color="text.secondary">
              Created: {formatDate(campaign.createdAt)}
            </Typography>
            {campaign.sentAt && (
              <Typography variant="body2" color="text.secondary">
                Sent: {formatDate(campaign.sentAt)}
              </Typography>
            )}
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateFollowUp}
          disabled={campaign.status !== 'SENT'}
        >
          Create Follow-up
        </Button>
      </Box>

      {/* Analytics Cards */}
      {analytics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 3,
                background: 'linear-gradient(135deg, #e3f2fd 0%, white 100%)',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {analytics.totalSent}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Sent
                    </Typography>
                  </Box>
                  <EmailIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 3,
                background: 'linear-gradient(135deg, #e8f5e8 0%, white 100%)',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {analytics.totalOpened}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Opened ({analytics.openRate}%)
                    </Typography>
                  </Box>
                  <ViewIcon sx={{ fontSize: 40, color: '#4caf50' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 3,
                background: 'linear-gradient(135deg, #fff3e0 0%, white 100%)',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {analytics.totalClicked}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Clicked ({analytics.clickRate}%)
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, color: '#ff9800' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f3e5f5 0%, white 100%)',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {followUps.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Follow-ups
                    </Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 40, color: '#9c27b0' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper
        sx={{
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden'
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '1rem',
              minHeight: 60,
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab label="Campaign Content" />
          <Tab label="Recipients" />
          <Tab label="Follow-ups" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tabValue === 0 && (
        <Paper
          sx={{
            p: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, #fafafa 0%, white 100%)',
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Campaign Content
          </Typography>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: 'text.primary' }}>
              Subject Line
            </Typography>
            <Paper
              sx={{
                p: 2,
                backgroundColor: '#f8f9fa',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {campaign.subject}
              </Typography>
            </Paper>
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: 'text.primary' }}>
              Email Content
            </Typography>
            <Paper
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 3,
                backgroundColor: 'white',
                maxHeight: '500px',
                overflow: 'auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              }}
              dangerouslySetInnerHTML={{ __html: campaign.content }}
            />
          </Box>
        </Paper>
      )}

      {tabValue === 1 && (
        <Paper
          sx={{
            p: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, #fafafa 0%, white 100%)',
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Recipients ({recipients.length})
          </Typography>
          <TableContainer
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'white',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Engagement</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recipients.map((recipient) => (
                  <TableRow
                    key={recipient.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{recipient.contact.email}</TableCell>
                    <TableCell>
                      {recipient.contact.firstName || recipient.contact.lastName
                        ? `${recipient.contact.firstName || ''} ${recipient.contact.lastName || ''}`.trim()
                        : '-'
                      }
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="Sent"
                        color="success"
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {recipient.engagement.opened && (
                          <Chip
                            label="Opened"
                            color="success"
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        )}
                        {recipient.engagement.clicked && (
                          <Chip
                            label="Clicked"
                            color="info"
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        )}
                        {!recipient.engagement.opened && !recipient.engagement.clicked && (
                          <Chip
                            label="No engagement"
                            color="default"
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {tabValue === 2 && (
        <Paper
          sx={{
            p: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, #fafafa 0%, white 100%)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Follow-up Campaigns ({followUps.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateFollowUp}
              disabled={campaign.status !== 'SENT'}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
              }}
            >
              Create Follow-up
            </Button>
          </Box>

          {followUps.length === 0 ? (
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'info.light',
                backgroundColor: '#e3f2fd',
              }}
            >
              No follow-up campaigns created yet. Create one to target specific recipient segments.
            </Alert>
          ) : (
            <TableContainer
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'white',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sent</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Opened</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Clicked</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {followUps.map((followUp) => (
                    <TableRow
                      key={followUp.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{followUp.name}</TableCell>
                      <TableCell>{followUp.subject}</TableCell>
                      <TableCell>
                        <Chip
                          label={followUp.status}
                          color={getStatusColor(followUp.status) as any}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{followUp.analytics.totalSent}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {followUp.analytics.totalOpened} ({followUp.analytics.openRate}%)
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {followUp.analytics.totalClicked} ({followUp.analytics.clickRate}%)
                      </TableCell>
                      <TableCell>{formatDate(followUp.createdAt)}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          sx={{
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'white',
                            },
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{
                            '&:hover': {
                              backgroundColor: 'secondary.light',
                              color: 'white',
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Follow-up Creation Dialog */}
      <Dialog open={followUpDialogOpen} onClose={() => setFollowUpDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Follow-up Campaign</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Campaign Name"
                value={followUpName}
                onChange={(e) => setFollowUpName(e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Target Recipients</InputLabel>
                <Select
                  value={recipientFilter}
                  onChange={(e) => handleRecipientFilterChange(e.target.value)}
                  label="Target Recipients"
                >
                  <MenuItem value="all">All Recipients</MenuItem>
                  <MenuItem value="opened">Opened</MenuItem>
                  <MenuItem value="clicked">Clicked</MenuItem>
                  <MenuItem value="unopened">Not Opened</MenuItem>
                  <MenuItem value="opened_not_clicked">Opened but Not Clicked</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {filteredRecipients.length} recipients will receive this follow-up
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={followUpStatus}
                  onChange={(e) => setFollowUpStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="DRAFT">Save as Draft</MenuItem>
                  <MenuItem value="SENDING">Send Immediately</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Template (Optional)</InputLabel>
                <Select
                  value={followUpTemplate}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  label="Template (Optional)"
                >
                  <MenuItem value="">No Template</MenuItem>
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={followUpSubject}
                onChange={(e) => setFollowUpSubject(e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Content *
              </Typography>
              <TiptapEditor
                content={followUpContent}
                onChange={setFollowUpContent}
                placeholder="Enter your follow-up message content. Use {{variableName}} for personalization."
                minHeight={200}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFollowUpDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateFollowUpSubmit}
            variant="contained"
            disabled={!followUpName || !followUpSubject || !followUpContent}
          >
            {followUpStatus === 'DRAFT' ? 'Save Draft' : 'Create & Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignDetail;
